import { ReactElement, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { isArray } from 'lodash-es'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator, ErrorState } from '@ui/feedback'
import { NavbarPortalCenter, NavbarProjectName } from '@ui/navigation'
import { useSnackbar, SnackbarKey } from '@ui/snackbar'
import {
  ElementSelectionStoreProvider,
  ResultsStoreProvider,
  ElementLoadStoreProvider,
  EditElementStoreProvider,
  StructuralPlanningStoreProvider,
  ControlStoreProvider,
  ModelStoreProvider,
  SystemManagerStoreProvider,
  TransmissionStoreProvider,
  CommentsStoreProvider,
  TapelineStoreProvider,
  PlanImageCardStoreProvider,
  PlanTableStoreProvider,
  EditorViewStoreProvider,
  SelectionStoreProvider,
} from '@editorStores'
import { AIChatSceneBridge, AIInstructionNoteButton, PropertiesPopup } from '@editorComponents'
import {
  getModel,
  getProjectAssemblies,
  getProject,
  getVerticalTransmissionGraph,
  getTensileTransmissionGraph,
} from '@queries'
import SnackbarCloseButton from 'src/components/ui/snackbar/SnackbarCloseButton'
import SnackbarProviderWithSceneAnchor from 'src/components/ui/snackbar/SnackbarProviderWithSceneAnchor'
import { useReducedNavigation, usePersistentParams } from 'src/hooks'
import { useQueriesLoading } from 'src/state/hooks'
import {
  getProjectConnectionPrioritization,
  getProjectConnections,
} from 'src/state/queries/connections'
import { deriveAssemblyAssignment } from 'src/state/utils/deriveElementAssignments'
import EditProjectDialog from './components/EditProjectDialog'
import ProcessNavigation from './components/ProcessNavigation'
import SelectionModelSync from './components/SelectionModelSync'
import SmartSuggestionsController from './components/SmartSuggestionsController'
import UnsavedActionsReloadGuard from './components/UnsavedActionsReloadGuard'
import { useAdoptProjectOrganisation, useEditorNavigation } from './hooks'
import { SceneStoreProvider } from './stores/sceneStore'

const closeNotificationAction = (key: SnackbarKey) => <SnackbarCloseButton snackbarKey={key} />

const Editor = (): ReactElement => {
  useReducedNavigation()
  useEditorNavigation()
  const { enqueueSnackbar } = useSnackbar()
  const onError = () => enqueueSnackbar('Fehler beim Laden der Projektdaten', { variant: 'error' })
  const { projectId }: { projectId?: string } = usePersistentParams()

  const adoptProjectOrganisation = useAdoptProjectOrganisation(projectId)

  // be carefull when editing the queries (do not change the order) and also
  // edit the queryResults below
  const queryResults = useQueries({
    queries: [
      {
        queryKey: getModel.getKey(projectId),
        queryFn: () => getModel.request(projectId),
        onError,
      },
      {
        queryKey: getProjectAssemblies.getKey(projectId),
        queryFn: () => getProjectAssemblies.request(projectId),
        onError,
      },
      {
        queryKey: getProjectConnections.getKey(projectId),
        queryFn: () => getProjectConnections.request(projectId),
        onError,
      },
      {
        queryKey: getProjectConnectionPrioritization.getKey(projectId),
        queryFn: () => getProjectConnectionPrioritization.request(projectId),
        onError,
      },
      {
        queryKey: getProject.getKey(projectId),
        queryFn: () => getProject.request(projectId),
        onSuccess: adoptProjectOrganisation,
        onError,
      },
      {
        queryKey: getVerticalTransmissionGraph.getKey(projectId),
        queryFn: () => getVerticalTransmissionGraph.request(projectId),
        onError: (error: unknown) =>
          enqueueSnackbar(
            buildErrorMessage(
              error as AxiosError,
              'Fehler beim Laden der Daten der vertikalen Transmission',
            ),
            {
              variant: 'error',
            },
          ),
      },
      {
        queryKey: getTensileTransmissionGraph.getKey(projectId),
        queryFn: () => getTensileTransmissionGraph.request(projectId),
        onError: (error: unknown) =>
          enqueueSnackbar(
            buildErrorMessage(
              error as AxiosError,
              'Fehler beim Laden der Daten für die Zugkraftweiterleitung',
            ),
            {
              variant: 'error',
            },
          ),
      },
    ],
  })

  const queryClient = useQueryClient()
  useEffect(
    () => () => {
      queryClient.removeQueries({
        predicate: el => isArray(el.queryKey) && el.queryKey.includes(projectId),
      })
    },
    [projectId, queryClient],
  )

  const isLoading = useQueriesLoading(queryResults)

  const [
    model,
    assemblies,
    connections,
    connectionPrioritization,
    project,
    verticalTransmissionGraph,
    tensileTransmissionGraph,
  ] = queryResults

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!model.data) {
    return (
      <>
        <ErrorState error="Bitte importieren Sie zunächst Ihr Modell" />
        <NavbarPortalCenter>
          <NavbarProjectName name={project.data?.name} hideBelowWidth={2000} />
        </NavbarPortalCenter>
      </>
    )
  }

  const assemblyAssignment = deriveAssemblyAssignment(model.data)

  return (
    <ModelStoreProvider
      // The store also holds the project: re-initialise on either refetch
      // (e.g. a rename by the AI chat), not only when the model changes.
      refreshAt={Math.max(model.dataUpdatedAt, project.dataUpdatedAt)}
      model={model.data}
      project={project.data}
    >
      <SystemManagerStoreProvider
        assemblies={assemblies.data}
        assemblyAssignment={assemblyAssignment}
        connections={connections.data}
        prioritization={connectionPrioritization.data}
      >
        <SceneStoreProvider>
          <ControlStoreProvider>
            <SnackbarProviderWithSceneAnchor action={closeNotificationAction}>
              <ElementSelectionStoreProvider>
                <SelectionStoreProvider>
                  <EditorViewStoreProvider>
                    <StructuralPlanningStoreProvider>
                      <CommentsStoreProvider>
                        <TapelineStoreProvider>
                          <PlanTableStoreProvider>
                            <PlanImageCardStoreProvider>
                              <TransmissionStoreProvider
                                tensileTransmissionGraph={tensileTransmissionGraph.data}
                                verticalTransmissionGraph={verticalTransmissionGraph.data}
                              >
                                <ResultsStoreProvider>
                                  <ElementLoadStoreProvider>
                                    <EditElementStoreProvider>
                                      <Outlet />
                                      <ProcessNavigation />
                                      <SelectionModelSync />
                                      <PropertiesPopup />
                                      <SmartSuggestionsController />
                                      <UnsavedActionsReloadGuard />
                                      <AIInstructionNoteButton />
                                      <AIChatSceneBridge />
                                      <EditProjectDialog />
                                      <NavbarPortalCenter>
                                        <NavbarProjectName
                                          name={project.data?.name}
                                          hideBelowWidth={1801}
                                        />
                                      </NavbarPortalCenter>
                                    </EditElementStoreProvider>
                                  </ElementLoadStoreProvider>
                                </ResultsStoreProvider>
                              </TransmissionStoreProvider>
                            </PlanImageCardStoreProvider>
                          </PlanTableStoreProvider>
                        </TapelineStoreProvider>
                      </CommentsStoreProvider>
                    </StructuralPlanningStoreProvider>
                  </EditorViewStoreProvider>
                </SelectionStoreProvider>
              </ElementSelectionStoreProvider>
            </SnackbarProviderWithSceneAnchor>
          </ControlStoreProvider>
        </SceneStoreProvider>
      </SystemManagerStoreProvider>
    </ModelStoreProvider>
  )
}

export default Editor
